import { useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { CodeBlock } from "@/components/cinch/CodeBlock";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { SearchHit } from "../api";

/**
 * One retrieved chunk. Shared by the Search results list and the Ask tab's
 * citations, so a citation and a search hit always read identically.
 *
 * The body is collapsed by default: `window`-type chunks can span ~1,000 lines
 * of a single file, and ten of those expanded at once buries the ranking.
 */
export function HitCard({
  hit,
  index,
  defaultOpen = false,
}: {
  hit: SearchHit;
  index?: number;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const lines =
    hit.line_start !== null && hit.line_end !== null
      ? `:${hit.line_start}-${hit.line_end}`
      : "";
  const path = `${hit.repo}/${hit.file_path}${lines}`;

  return (
    <div className="rounded-lg border">
      <div className="flex items-start gap-3 p-3">
        <Button
          variant="ghost"
          size="icon"
          className="mt-0.5 h-6 w-6 shrink-0"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Collapse snippet" : "Expand snippet"}
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {index !== undefined ? (
              <span className="text-xs tabular-nums text-muted-foreground">
                [{index}]
              </span>
            ) : null}
            <span className="break-all font-mono text-sm">{path}</span>
            {hit.function_name ? (
              <span className="text-xs text-muted-foreground">
                {hit.function_name}
              </span>
            ) : null}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary" className="tabular-nums">
              {hit.score.toFixed(3)}
            </Badge>
            {hit.language ? <Badge variant="outline">{hit.language}</Badge> : null}
            {hit.chunk_type ? <Badge variant="outline">{hit.chunk_type}</Badge> : null}
            <span className="text-xs text-muted-foreground">
              {hit.last_author ?? "unknown"}
              {hit.last_modified ? ` · ${hit.last_modified.slice(0, 10)}` : ""}
            </span>
          </div>

          {hit.last_commit_message ? (
            <div
              className="mt-1 truncate text-xs text-muted-foreground"
              title={hit.last_commit_message}
            >
              {hit.last_commit_message}
            </div>
          ) : null}
        </div>
      </div>

      {open && hit.content ? (
        <div className="border-t p-3">
          <CodeBlock code={hit.content} label={path} />
        </div>
      ) : null}
    </div>
  );
}
